import { getIdpBaseUrl } from '../api';

/** One release entry as returned by the IDP customer-feed API. */
export type ReleaseNotesEntry = {
  id: string;
  name: string;
  signed_off_at: string;
  source: 'base' | 'customer';
  released: boolean;
  status: string;
  markdown: string;
};

/** Response shape of `GET /api/v1/releases/customer-feed`. */
export type CustomerReleaseFeedResponse = {
  customer: string;
  tenant_id: string | null;
  count: number;
  releases: ReleaseNotesEntry[];
  markdown: string;
  /** Pagination metadata — present only when the request passes `limit`. */
  total?: number;
  limit?: number | null;
  offset?: number;
  has_more?: boolean;
};

export type FetchCustomerReleaseFeedParams = {
  tenantId: string;
  customer: string;
  /** Bearer token to authenticate against the IDP API (e.g. the app's own Auth0 access token). */
  token?: string;
  /** Overrides `getIdpBaseUrl()` / `CTY_IDP_API_URL` for this call. */
  baseUrl?: string;
  /** Page size for the customer-feed request; omit to fetch the API's unpaginated default. */
  limit?: number;
  /** Row offset for the customer-feed request; omit (or 0) for the first page. */
  offset?: number;
  signal?: AbortSignal;
};

const CUSTOMER_FEED_PATH = '/api/v1/releases/customer-feed';

/**
 * GET the per-customer release feed from the IDP service. Throws on missing config, missing
 * params, or a non-2xx response — callers are expected to fall back to their existing
 * build-number display when this rejects.
 */
export async function fetchCustomerReleaseFeed(params: FetchCustomerReleaseFeedParams): Promise<CustomerReleaseFeedResponse> {
  const base = (params.baseUrl ?? getIdpBaseUrl()).trim().replace(/\/+$/, '');
  if (!base) {
    throw new Error('IDP base URL is not configured. Set CTY_IDP_API_URL or call setIdpConfig({ baseUrl }).');
  }
  if (!params.tenantId || !params.customer) {
    throw new Error('tenantId and customer are required to fetch the release notes feed.');
  }

  const query = new URLSearchParams({ tenant_id: params.tenantId, customer: params.customer });
  if (params.limit != null) {
    query.set('limit', String(params.limit));
  }
  if (params.offset) {
    query.set('offset', String(params.offset));
  }
  const response = await fetch(`${base}${CUSTOMER_FEED_PATH}?${query.toString()}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(params.token ? { Authorization: `Bearer ${params.token}` } : {}),
    },
    signal: params.signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`customer-feed failed: ${response.status} ${errText}`);
  }

  return (await response.json()) as CustomerReleaseFeedResponse;
}
